'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@cobrastyle/ui';

/**
 * How much of a description is shown before the control appears.
 *
 * Roughly fifteen lines at the accordion's 14.5px/1.7 rhythm — enough that a
 * normal product reads in full and is never clamped, and that a long one shows
 * its opening paragraph rather than a teaser. Exported for the test, which
 * cannot measure anything itself.
 */
export const COLLAPSED_HEIGHT = 320;

interface ReadMoreProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Clamps long body copy to a fixed height with a "Läs mer" control (p.2 of the
 * "Hemsidan 3.0" feedback: the Aimpoint description runs for a full screen
 * before anything else on the page is reachable).
 *
 * Height only — nothing is removed from the document. The description is
 * server-rendered PageBuilder or plain HTML and arrives here as an opaque
 * node, so this component never looks at the content: it cannot truncate at a
 * word or a paragraph without parsing markup it does not own, and the version
 * that does not parse is also the one a crawler and an in-page search still
 * see in full.
 *
 * Whether to clamp at all is measured rather than guessed, because the same
 * component wraps a two-line description and a two-page one, and a control
 * that reveals nothing is worse than no control.
 */
export function ReadMore({ children, className }: ReadMoreProps) {
  const t = useTranslations('product');
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = bodyRef.current;
    if (!element) return;

    // scrollHeight is the full content height even while max-height clamps the
    // box, so this reads the same whether open or closed.
    const measure = () => setOverflows(element.scrollHeight > COLLAPSED_HEIGHT);
    measure();

    // A PageBuilder description carries images, which settle after first
    // paint and change the answer. Without this the control is decided
    // against the text alone and then never revisited.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [children]);

  const clamped = overflows && !expanded;

  return (
    <div className={className}>
      <div
        ref={bodyRef}
        // Inline, not a Tailwind class: the height is a measured number shared
        // with the effect above, and two places holding the same figure is how
        // the control and the clamp drift apart.
        style={clamped ? { maxHeight: COLLAPSED_HEIGHT } : undefined}
        className={cn('relative', clamped && 'overflow-hidden')}
      >
        {children}

        {/* Marks the cut as a cut. Without it the text simply stops mid-word
            and reads as a rendering fault rather than as more to come. */}
        {clamped && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-background to-transparent"
          />
        )}
      </div>

      {overflows && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          className="mt-3 text-sm font-medium text-accent-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
        >
          {expanded ? t('readLess') : t('readMore')}
        </button>
      )}
    </div>
  );
}
